/**
 * Client-side search/filter over rows a table has already loaded (Batch 6, R6). Pure: the caller
 * owns the state and the rendering; hidden rows are simply absent from the result, so anything
 * that acts on "the rows" (select-all, CSV) acts on the visible ones.
 */

export interface TableFilter {
  /** Free text; matched against every field `accessors.text` returns. */
  query?: string;
  /** Exact value of `accessors.status`; '' or 'all' means no filter. */
  status?: string;
  /** Exact value of `accessors.group` (e.g. a product); '' or 'all' means no filter. */
  group?: string;
}

export interface TableFilterAccessors<T> {
  text: (row: T) => ReadonlyArray<string | null | undefined>;
  status?: (row: T) => string | null | undefined;
  group?: (row: T) => string | null | undefined;
}

/** Lowercase, strip diacritics, trim. "Zoë" and "zoe" fold to the same string. */
export function foldText(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

const isSet = (value: string | undefined) => !!value && value !== 'all';

export function hasActiveFilter(filter: TableFilter): boolean {
  return foldText(filter.query ?? '') !== '' || isSet(filter.status) || isSet(filter.group);
}

export function filterRows<T>(
  rows: readonly T[],
  filter: TableFilter,
  accessors: TableFilterAccessors<T>,
): T[] {
  const query = foldText(filter.query ?? '');
  const status = isSet(filter.status) ? filter.status : null;
  const group = isSet(filter.group) ? filter.group : null;
  return rows.filter((row) => {
    if (status !== null && accessors.status?.(row) !== status) return false;
    if (group !== null && accessors.group?.(row) !== group) return false;
    if (query === '') return true;
    return accessors.text(row).some((field) => !!field && foldText(field).includes(query));
  });
}
